import { useEffect, useRef } from 'react';
import type React from 'react';
import { UseFormReset } from 'react-hook-form';
import { FilterState } from '@/types/filter';
import { FilterDraft, SectionId } from '../types/filterDraft.types';

interface UseFilterInitializationProps {
  isVisible: boolean;
  filterState: FilterState;
  getDraft: () => FilterDraft | null;
  getSectionsWithData: () => SectionId[];
  reset: UseFormReset<FilterState>;
  setHasDraft: (hasDraft: boolean) => void;
}

interface UseFilterInitializationReturn {
  isInitializedRef: React.RefObject<boolean>;
}

export const useFilterInitialization = ({
  isVisible,
  filterState,
  getDraft,
  getSectionsWithData,
  reset,
  setHasDraft,
}: UseFilterInitializationProps): UseFilterInitializationReturn => {
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isVisible) {
      isInitializedRef.current = false;
      return;
    }

    if (isInitializedRef.current) return;

    const draft = getDraft();
    const activeSections = getSectionsWithData();

    if (draft && activeSections.length > 0) {
      const mergedValues = { ...filterState };

      for (const sectionId of activeSections) {
        const sectionData = draft[sectionId];
        if (sectionData) {
          Object.assign(mergedValues, sectionData);
        }
      }

      reset(mergedValues, { keepDefaultValues: false });
      setHasDraft(true);
    } else {
      reset(filterState, { keepDefaultValues: false });
      setHasDraft(false);
    }

    isInitializedRef.current = true;
  }, [isVisible, filterState, getDraft, getSectionsWithData, reset, setHasDraft]);

  return { isInitializedRef };
};
